import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Send } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = { title: "", body: "", image: "", published: true, send_newsletter: false };

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);

  const load = () => {
    api.get("/announcements/all").then((r) => setItems(r.data)).catch(() => {});
    api.get("/newsletter-log").then((r) => setLog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, send_newsletter: false }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = editing
        ? await api.put(`/announcements/${editing.id}`, form)
        : await api.post("/announcements", form);
      if (form.send_newsletter && form.published && resp.data?._newsletter_queued) {
        toast.success("Zapisano. Newsletter jest wysyłany w tle — postęp zobaczysz w sekcji „Historia wysyłek”.");
      } else {
        toast.success("Zapisano");
      }
      setOpen(false);
      load();
      setTimeout(load, 5000);
    } catch { toast.error("Błąd zapisu"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Usunąć tę aktualność?")) return;
    await api.delete(`/announcements/${id}`);
    toast.success("Usunięto");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Aktualności</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ogłoszenia wyświetlane na stronie głównej</p>
        </div>
        <Button onClick={openNew} className="gap-1.5 bg-ztk-blue" data-testid="news-add"><Plus className="h-4 w-4" /> Nowa aktualność</Button>
      </div>

      <div className="mt-8 space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Brak aktualności.</p>}
        {items.map((a) => (
          <div key={a.id} className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card p-4" data-testid={`admin-news-${a.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              {a.image ? <img src={fileUrl(a.image)} alt="" className="h-12 w-12 rounded-lg object-cover" /> : null}
              <div className="min-w-0">
                <h3 className="truncate font-display font-semibold text-ztk-navy dark:text-white">{a.title}</h3>
                <p className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("pl-PL")}</p>
              </div>
              {a.published
                ? <Badge className="gap-1 bg-emerald-500 hover:bg-emerald-500"><Eye className="h-3 w-3" /> Opublikowana</Badge>
                : <Badge variant="secondary" className="gap-1"><EyeOff className="h-3 w-3" /> Ukryta</Badge>}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`news-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(a.id)} className="text-destructive" data-testid={`news-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {log.length > 0 && (
        <div className="mt-12">
          <h3 className="mb-3 font-display text-lg font-semibold text-ztk-navy dark:text-white">Historia wysyłek newslettera</h3>
          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left">
                <tr><th className="px-4 py-2.5 font-medium">Tytuł</th><th className="px-4 py-2.5 font-medium">Odbiorcy</th><th className="px-4 py-2.5 font-medium">Data</th></tr>
              </thead>
              <tbody>
                {log.map((l) => (
                  <tr key={l.id} className="border-t border-border bg-card" data-testid={`newsletter-log-${l.id}`}>
                    <td className="px-4 py-2.5 font-medium text-ztk-navy dark:text-white">{l.title}</td>
                    <td className="px-4 py-2.5"><span className={l.sent === 0 && l.total > 0 ? "font-medium text-destructive" : l.sent < l.total ? "font-medium text-ztk-yellow" : "font-medium text-emerald-600"}>{l.sent} / {l.total}</span></td>
                    <td className="px-4 py-2.5 text-muted-foreground">{new Date(l.created_at).toLocaleString("pl-PL")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edytuj aktualność" : "Nowa aktualność"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Tytuł</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title" className="mt-1.5" /></div>
            <div><Label>Treść</Label><div className="mt-1.5"><RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} testid="news-body" placeholder="Treść ogłoszenia..." /></div></div>
            <ImageUpload value={form.image} multiple={false} label="Zdjęcie (opcjonalne)" onChange={(v) => setForm({ ...form, image: v })} />
            <div className="flex items-center gap-2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="news-published" />
              <span className="text-sm">Opublikowana (widoczna na stronie)</span>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-ztk-blue/30 bg-ztk-blue/5 p-3">
              <Switch checked={!!form.send_newsletter} onCheckedChange={(v) => setForm({ ...form, send_newsletter: v })} data-testid="news-newsletter" className="mt-0.5" />
              <div>
                <span className="flex items-center gap-1.5 text-sm font-medium text-ztk-navy dark:text-white"><Send className="h-3.5 w-3.5 text-ztk-blue" /> Wyślij jako e-mail do wszystkich członków</span>
                <p className="text-xs text-muted-foreground">Newsletter zostanie wysłany do zatwierdzonych członków po zapisaniu (tylko gdy opublikowana).</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-ztk-blue" data-testid="news-save">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Zapisz</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
